/**
 * Client-side study ↔ session bindings until all rows carry study_id server-side.
 * Merges with ConversationEntry.study_id from the conversation index when present.
 */

import type { ConversationEntry } from '@/types/chat'
import { getStudyBySlug, listStudies } from '@/components/study-design/mock-data'
import type { StudyChatContext } from '@/lib/study-chat-context'

export function resolveStudyId(studySlug: string, userId?: string | null): string {
  return getStudyBySlug(studySlug, userId)?.id ?? studySlug
}

/** Detect study from free text (titles, canvas headings) — beats corrupted study_id. */
export function inferStudyIdFromText(text: string, userId?: string | null): string | null {
  const upper = text.toUpperCase()
  for (const study of listStudies(userId)) {
    if (upper.includes(study.code)) return study.id
  }
  return null
}

type CanvasHint = { sessionId: string; title: string; updatedAt?: string }

export function contentStudyIdForSession(
  sessionId: string,
  conversations: ConversationEntry[],
  canvasItems?: CanvasHint[],
  userId?: string | null,
): string | null {
  const entry = conversations.find(c => c.session_id === sessionId)
  if (entry?.title) {
    const fromTitle = inferStudyIdFromText(entry.title, userId)
    if (fromTitle) return fromTitle
  }

  for (const item of canvasItems ?? []) {
    if (item.sessionId !== sessionId) continue
    const inferred = inferStudyIdFromText(item.title, userId)
    if (inferred) return inferred
  }

  // Do not fall back to entry.study_id — corrupted by the pre-scoping auto-bind bug.
  return null
}

function threadRegistryKey(userId: string, studySlug: string) {
  return `crucible_study_thread_ids_${userId}_${studySlug}`
}

export function getRegisteredThreadIds(
  userId: string | null | undefined,
  studySlug: string,
): string[] {
  if (!userId || typeof sessionStorage === 'undefined') return []
  try {
    const raw = sessionStorage.getItem(threadRegistryKey(userId, studySlug))
    const ids: string[] = raw ? JSON.parse(raw) : []
    return ids.filter(id => id.startsWith('session_'))
  } catch {
    return []
  }
}

export function registerStudyThread(
  userId: string | null | undefined,
  studySlug: string,
  sessionId: string,
): void {
  if (!userId || typeof sessionStorage === 'undefined' || !sessionId.startsWith('session_')) return
  bindSessionToStudy(userId, studySlug, sessionId)
  rememberActiveStudyThread(userId, studySlug, sessionId)
  try {
    const key = threadRegistryKey(userId, studySlug)
    const ids = getRegisteredThreadIds(userId, studySlug)
    if (!ids.includes(sessionId)) {
      sessionStorage.setItem(key, JSON.stringify([sessionId, ...ids]))
    }
  } catch {
    // ignore
  }
}

export function unregisterStudyThread(
  userId: string | null | undefined,
  studySlug: string,
  sessionId: string,
): void {
  if (!userId || typeof sessionStorage === 'undefined') return
  try {
    const key = threadRegistryKey(userId, studySlug)
    const next = getRegisteredThreadIds(userId, studySlug).filter(id => id !== sessionId)
    sessionStorage.setItem(key, JSON.stringify(next))
  } catch {
    // ignore
  }
  unbindSessionFromStudy(userId, sessionId)
}

export function isSessionForStudy(
  userId: string | null | undefined,
  sessionId: string,
  studySlug: string,
  conversations: ConversationEntry[],
  canvasItems?: CanvasHint[],
): boolean {
  const expected = resolveStudyId(studySlug, userId)
  const contentStudy = contentStudyIdForSession(sessionId, conversations, canvasItems, userId)
  if (contentStudy) return contentStudy === expected

  if (discoverLegacySessions(studySlug, conversations, canvasItems, userId).includes(sessionId)) {
    return true
  }

  const registered = getRegisteredThreadIds(userId, studySlug)
  if (registered.includes(sessionId)) {
    const entry = conversations.find(c => c.session_id === sessionId)
    if (entry && (entry.has_canvas || entry.message_count > 0)) {
      return true
    }
    if (!entry) {
      return getLocalStudyIdForSession(userId, sessionId) === expected
    }
  }

  return false
}

/** Best matching indexed session for a study based on canvas/title cues. */
export function discoverLegacySessions(
  studySlug: string,
  conversations: ConversationEntry[],
  canvasItems?: CanvasHint[],
  userId?: string | null,
): string[] {
  const expected = resolveStudyId(studySlug, userId)
  const ranked = new Map<string, number>()

  const bump = (sessionId: string, ts: number) => {
    ranked.set(sessionId, Math.max(ranked.get(sessionId) ?? 0, ts))
  }

  for (const c of conversations) {
    const contentStudy = contentStudyIdForSession(c.session_id, conversations, canvasItems, userId)
    if (contentStudy === expected) {
      bump(c.session_id, new Date(c.updated_at).getTime())
      continue
    }
    // Trust server study_id when canvas/title don't contradict.
    if (c.study_id === expected && contentStudy !== null && contentStudy !== expected) continue
    if (c.study_id === expected) {
      bump(c.session_id, new Date(c.updated_at).getTime())
    }
  }

  for (const item of canvasItems ?? []) {
    if (inferStudyIdFromText(item.title, userId) !== expected) continue
    bump(item.sessionId, new Date(item.updatedAt ?? 0).getTime() || Date.now())
  }

  if (userId) {
    for (const sessionId of getRegisteredThreadIds(userId, studySlug)) {
      const entry = conversations.find(c => c.session_id === sessionId)
      if (!entry || (!entry.has_canvas && entry.message_count === 0)) continue
      const contentStudy = contentStudyIdForSession(sessionId, conversations, canvasItems, userId)
      if (contentStudy !== null && contentStudy !== expected) continue
      bump(sessionId, new Date(entry.updated_at).getTime())
    }
  }

  return [...ranked.entries()]
    .sort((a, b) => b[1] - a[1])
    .map(([sessionId]) => sessionId)
}

export function findBestLegacyDeskSession(
  studySlug: string,
  conversations: ConversationEntry[],
  canvasItems?: CanvasHint[],
): string | null {
  return discoverLegacySessions(studySlug, conversations, canvasItems)[0] ?? null
}

export function isVacantDeskSession(
  sessionId: string,
  conversations: ConversationEntry[],
  canvasItems?: CanvasHint[],
): boolean {
  const entry = conversations.find(c => c.session_id === sessionId)
  const hasCanvasInLibrary = (canvasItems ?? []).some(i => i.sessionId === sessionId)
  if (hasCanvasInLibrary) return false
  if (entry?.has_canvas) return false
  if (entry && entry.message_count > 0) return false
  if (!entry) return true
  return !entry.has_canvas && entry.message_count === 0
}

export function backfillStudyThreadRegistry(
  userId: string | null | undefined,
  studySlug: string,
  sessionIds: string[],
): void {
  for (const sessionId of sessionIds) {
    registerStudyThread(userId, studySlug, sessionId)
  }
}

/** @deprecated use findBestLegacyDeskSession */
export function findLegacyDeskSession(
  studySlug: string,
  conversations: ConversationEntry[],
  canvasItems?: CanvasHint[],
): string | null {
  return findBestLegacyDeskSession(studySlug, conversations, canvasItems)
}

function mapKey(userId: string) {
  return `crucible_study_thread_map_${userId}`
}

function activeKey(userId: string, studySlug: string) {
  return `crucible_study_thread_active_${userId}_${studySlug}`
}

export function bindSessionToStudy(
  userId: string | null | undefined,
  studySlug: string,
  sessionId: string,
): void {
  if (!userId || typeof sessionStorage === 'undefined' || !sessionId.startsWith('session_')) return
  const studyId = resolveStudyId(studySlug, userId)
  try {
    const raw = sessionStorage.getItem(mapKey(userId))
    const map: Record<string, string> = raw ? JSON.parse(raw) : {}
    map[sessionId] = studyId
    sessionStorage.setItem(mapKey(userId), JSON.stringify(map))
    sessionStorage.setItem(activeKey(userId, studySlug), sessionId)
  } catch {
    // ignore quota / private mode
  }
}

export function rememberActiveStudyThread(
  userId: string | null | undefined,
  studySlug: string,
  sessionId: string,
): void {
  if (!userId || typeof sessionStorage === 'undefined') return
  sessionStorage.setItem(activeKey(userId, studySlug), sessionId)
}

export function clearActiveStudyThread(
  userId: string | null | undefined,
  studySlug: string,
): void {
  if (!userId || typeof sessionStorage === 'undefined') return
  sessionStorage.removeItem(activeKey(userId, studySlug))
}

export function getActiveStudyThread(
  userId: string | null | undefined,
  studySlug: string,
): string | null {
  if (!userId || typeof sessionStorage === 'undefined') return null
  const stored = sessionStorage.getItem(activeKey(userId, studySlug))
  return stored?.startsWith('session_') ? stored : null
}

export function getLocalStudyIdForSession(
  userId: string | null | undefined,
  sessionId: string,
): string | null {
  if (!userId || typeof sessionStorage === 'undefined') return null
  try {
    const raw = sessionStorage.getItem(mapKey(userId))
    if (!raw) return null
    const map: Record<string, string> = JSON.parse(raw)
    return map[sessionId] ?? null
  } catch {
    return null
  }
}

/** Drop a client-only binding (e.g. after a mistaken auto-bind on study switch). */
export function unbindSessionFromStudy(
  userId: string | null | undefined,
  sessionId: string,
): void {
  if (!userId || typeof sessionStorage === 'undefined') return
  try {
    const raw = sessionStorage.getItem(mapKey(userId))
    if (!raw) return
    const map: Record<string, string> = JSON.parse(raw)
    if (!(sessionId in map)) return
    delete map[sessionId]
    sessionStorage.setItem(mapKey(userId), JSON.stringify(map))
  } catch {
    // ignore
  }
}

/** Align local bindings with server-indexed study_id values. */
export function syncStudyBindingsFromIndex(
  userId: string | null | undefined,
  conversations: ConversationEntry[],
): void {
  if (!userId || typeof sessionStorage === 'undefined') return
  try {
    const raw = sessionStorage.getItem(mapKey(userId))
    const map: Record<string, string> = raw ? JSON.parse(raw) : {}
    let changed = false
    for (const entry of conversations) {
      if (entry.study_id && map[entry.session_id] !== entry.study_id) {
        map[entry.session_id] = entry.study_id
        changed = true
      }
    }
    if (changed) sessionStorage.setItem(mapKey(userId), JSON.stringify(map))
  } catch {
    // ignore
  }
}

export function studyIdForConversation(
  userId: string | null | undefined,
  entry: ConversationEntry,
): string | null {
  if (entry.study_id) return entry.study_id
  // Indexed rows without study_id are desk/orphan threads — not study-scoped.
  return null
}

export function sessionBelongsToStudy(
  userId: string | null | undefined,
  sessionId: string,
  studySlug: string,
  conversations: ConversationEntry[],
): boolean {
  const studyId = resolveStudyId(studySlug, userId)
  const entry = conversations.find(c => c.session_id === sessionId)
  if (entry) return entry.study_id === studyId
  // Unindexed session — only trust an explicit active-thread bind for this study.
  if (getLocalStudyIdForSession(userId, sessionId) !== studyId) return false
  return getActiveStudyThread(userId, studySlug) === sessionId
}

export function sessionMatchesStudyForList(
  sessionId: string,
  studySlug: string,
  conversations: ConversationEntry[],
  canvasItems?: CanvasHint[],
  userId?: string | null,
): boolean {
  const expected = resolveStudyId(studySlug, userId)
  const content = contentStudyIdForSession(sessionId, conversations, canvasItems, userId)
  if (content) return content === expected
  if (discoverLegacySessions(studySlug, conversations, canvasItems, userId).includes(sessionId)) {
    return true
  }
  const entry = conversations.find(c => c.session_id === sessionId)
  if (entry?.study_id === expected) return true
  if (userId && getRegisteredThreadIds(userId, studySlug).includes(sessionId)) {
    if (entry && (entry.has_canvas || entry.message_count > 0)) return true
  }
  return false
}

export function listStudyThreads(
  userId: string | null | undefined,
  studySlug: string,
  conversations: ConversationEntry[],
  canvasItems?: CanvasHint[],
): ConversationEntry[] {
  const ids = [
    ...new Set([
      ...getRegisteredThreadIds(userId, studySlug),
      ...discoverLegacySessions(studySlug, conversations, canvasItems, userId),
    ]),
  ]
  const byId = new Map(conversations.map(c => [c.session_id, c]))
  return ids
    .map(id => byId.get(id))
    .filter((c): c is ConversationEntry => {
      if (!c) return false
      return sessionMatchesStudyForList(c.session_id, studySlug, conversations, canvasItems, userId)
    })
    .sort((a, b) => new Date(b.updated_at).getTime() - new Date(a.updated_at).getTime())
}

export function pickStudyThreadSession(
  userId: string | null | undefined,
  studySlug: string,
  conversations: ConversationEntry[],
  currentSessionId: string,
): string | null {
  const threads = listStudyThreads(userId, studySlug, conversations)
  const saved = getActiveStudyThread(userId, studySlug)
  if (saved && sessionBelongsToStudy(userId, saved, studySlug, conversations)) {
    return saved
  }
  if (threads[0]) return threads[0].session_id
  if (sessionBelongsToStudy(userId, currentSessionId, studySlug, conversations)) {
    return currentSessionId
  }
  return null
}

/** Slug set by Study Design page while mounted — used when registering sessions. */
let activeStudySlugForRegistration: string | null = null
let activeUserIdForRegistration: string | null = null
let activeVersionIdForRegistration: string | null = null
let activeStudyContextForRegistration: StudyChatContext | null = null

export function setActiveStudySlugForRegistration(
  slug: string | null,
  userId?: string | null,
) {
  activeStudySlugForRegistration = slug
  if (userId !== undefined) {
    activeUserIdForRegistration = userId
  }
}

export function setActiveVersionIdForRegistration(versionId: string | null) {
  activeVersionIdForRegistration = versionId
}

export function setActiveStudyContextForRegistration(context: StudyChatContext | null) {
  activeStudyContextForRegistration = context
}

export function getActiveStudyContextForRegistration(): StudyChatContext | null {
  return activeStudyContextForRegistration
}

export function getActiveStudySlugForRegistration(): string | null {
  return activeStudySlugForRegistration
}

export function getActiveStudyIdForRegistration(): string | null {
  const slug = activeStudySlugForRegistration
  return slug ? resolveStudyId(slug, activeUserIdForRegistration) : null
}

export function getActiveStudyCodeForRegistration(): string | null {
  const slug = activeStudySlugForRegistration
  if (!slug) return null
  return getStudyBySlug(slug, activeUserIdForRegistration)?.code ?? null
}

export function getActiveVersionIdForRegistration(): string | null {
  return activeVersionIdForRegistration
}
